import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Crown, Target, Flame, Zap, TrendingUp, CheckCircle, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * `/match` — the Match Center.
 * Users predict winner + score for each upcoming fixture. Scoring:
 *   · 3 pts — exact score
 *   · 1 pt  — correct outcome
 *   · +streak bonus once you land 3+ exact scores in a row
 */
export default function MatchPredict() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [mine, setMine] = useState({ predictions: [], totals: null });
  const [board, setBoard] = useState([]);
  const [drafts, setDrafts] = useState({}); // fixtureId → { home, away }
  const [submitting, setSubmitting] = useState({});
  const [tab, setTab] = useState("upcoming");

  const [featured, setFeatured] = useState(null);
  const [polls, setPolls] = useState({}); // fixtureId → breakdown payload

  const loadPoll = async (fxId) => {
    try {
      const r = await api.get(`/fixtures/${fxId}/predict/breakdown`);
      setPolls((prev) => ({ ...prev, [fxId]: r.data }));
    } catch (e) { /* silent */ }
  };

  const load = async () => {
    try {
      const [t, f, b, ft] = await Promise.all([
        api.get("/teams"),
        api.get("/fixtures"),
        api.get("/predictions/leaderboard"),
        api.get("/fixtures/featured").catch(() => ({ data: null })),
      ]);
      setTeams(t.data || []);
      setFixtures(f.data || []);
      setBoard(b.data || []);
      setFeatured(ft.data && ft.data.fixture ? ft.data : null);
      if (user && typeof user === "object") {
        const m = await api.get("/me/predictions");
        setMine(m.data || { predictions: [], totals: null });
      }
      // Load community breakdown for every upcoming fixture — fire-and-forget.
      const upcomingIds = (f.data || []).filter((x) => x.status === "upcoming").map((x) => x.id);
      upcomingIds.forEach((id) => loadPoll(id));
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  const teamById = (id) => teams.find((t) => t.id === id);

  const upcoming = useMemo(
    () => fixtures.filter((f) => f.status === "upcoming").sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    [fixtures],
  );
  const completed = useMemo(
    () => fixtures.filter((f) => f.status === "completed").sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || "")),
    [fixtures],
  );

  const predictionByFxId = useMemo(() => {
    const map = {};
    (mine.predictions || []).forEach((p) => { map[p.fixture_id] = p; });
    return map;
  }, [mine]);

  const setDraft = (fxId, key, val) => setDrafts((d) => ({ ...d, [fxId]: { ...(d[fxId] || {}), [key]: val } }));

  const submit = async (fx) => {
    if (!isAuthed) {
      toast.error("Log in to lock in a prediction.");
      return;
    }
    const d = drafts[fx.id];
    if (!d || d.home === "" || d.away === "" || d.home === undefined || d.away === undefined) {
      toast.error("Enter both scores."); return;
    }
    setSubmitting((s) => ({ ...s, [fx.id]: true }));
    try {
      await api.post(`/fixtures/${fx.id}/predict`, {
        home_score: Number(d.home),
        away_score: Number(d.away),
      });
      toast.success("Prediction locked in.");
      await load();
      loadPoll(fx.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting((s) => ({ ...s, [fx.id]: false }));
    }
  };

  if (user === null) return <div className="text-gray-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  const isAuthed = user && typeof user === "object";

  const myRank = isAuthed ? board.findIndex((b) => b.user_id === user.id) + 1 : 0;
  const totals = mine.totals || { points: 0, exact: 0, correct: 0, count: 0, active_streak: 0 };

  return (
    <div className="space-y-8" data-testid="match-predict-root">
      {/* Hero */}
      <div className="border-l-4 border-[#F4D03F] pl-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">/ Match Center</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Match Predict</h1>
        <p className="text-sm text-gray-400 mt-2 max-w-2xl">
          Call the score before kickoff. <span className="text-[#00FFB2] font-bold">+3</span> for an exact
          score, <span className="text-cyan-400 font-bold">+1</span> for the correct outcome,
          and a <span className="text-[#FF007F] font-bold">streak bonus</span> once you land 3 exact scores in a row.
        </p>
      </div>

      {featured && featured.home && featured.away && (() => {
        const fx = featured.fixture;
        const home = featured.home;
        const away = featured.away;
        const existing = predictionByFxId[fx.id];
        const draft = drafts[fx.id] || { home: existing?.home_score ?? "", away: existing?.away_score ?? "" };
        const kickoff = new Date(fx.scheduled_at);
        const now = Date.now();
        const diff = Math.max(0, kickoff.getTime() - now);
        const hrs = Math.floor(diff / 3_600_000);
        const mins = Math.floor((diff % 3_600_000) / 60_000);
        const countdown = diff === 0 ? "LIVE" : hrs >= 24 ? `${Math.floor(hrs / 24)}d ${hrs % 24}h` : `${hrs}h ${mins}m`;
        return (
          <div
            data-testid="match-predict-featured"
            className="relative overflow-hidden border border-[#F4D03F]/50 bg-gradient-to-br from-[#F4D03F]/10 via-[#0a0a12] to-[#FF007F]/10 p-6 lg:p-8"
            style={{
              boxShadow: "0 0 40px rgba(244,208,63,0.15) inset, 0 0 25px rgba(255,0,127,0.08)",
            }}
          >
            {/* Grain */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
                 style={{ backgroundImage: "radial-gradient(#F4D03F 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
            <div className="relative flex flex-wrap items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#F4D03F] animate-pulse" />
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#F4D03F]">
                  Featured match · Circuit's hottest bet
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest">
                <span className="text-gray-400">
                  <span className="text-white font-bold">{featured.predictions_count}</span> pick{featured.predictions_count === 1 ? "" : "s"} locked
                </span>
                {fx.map && <span className="text-cyan-400">{fx.map}</span>}
                <span className="text-[#FF007F] font-bold">kickoff · {countdown}</span>
              </div>
            </div>

            <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-8">
              <div className="text-right">
                <div className="font-heading font-black text-3xl lg:text-5xl uppercase tracking-tighter leading-none" style={{ color: home.logo_color }}>
                  {home.tag}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400 truncate mt-1">
                  {home.name}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                  W{home.wins || 0} · L{home.losses || 0}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number" min="0" max="99"
                  value={draft.home}
                  onChange={(e) => setDraft(fx.id, "home", e.target.value)}
                  data-testid="match-predict-featured-home"
                  disabled={!isAuthed}
                  className="w-16 lg:w-20 bg-[#05050A] border-2 border-[#F4D03F]/60 focus:border-[#F4D03F] outline-none rounded-none py-2 text-center font-mono text-3xl lg:text-4xl font-black text-white disabled:opacity-40"
                />
                <span className="font-heading text-3xl lg:text-4xl font-black text-[#F4D03F]">:</span>
                <input
                  type="number" min="0" max="99"
                  value={draft.away}
                  onChange={(e) => setDraft(fx.id, "away", e.target.value)}
                  data-testid="match-predict-featured-away"
                  disabled={!isAuthed}
                  className="w-16 lg:w-20 bg-[#05050A] border-2 border-[#F4D03F]/60 focus:border-[#F4D03F] outline-none rounded-none py-2 text-center font-mono text-3xl lg:text-4xl font-black text-white disabled:opacity-40"
                />
              </div>

              <div>
                <div className="font-heading font-black text-3xl lg:text-5xl uppercase tracking-tighter leading-none" style={{ color: away.logo_color }}>
                  {away.tag}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400 truncate mt-1">
                  {away.name}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                  W{away.wins || 0} · L{away.losses || 0}
                </div>
              </div>
            </div>

            {existing && (
              <div className="relative text-[10px] font-mono uppercase tracking-widest text-gray-400 text-center mt-4">
                Currently locked: <span className="text-cyan-400">{existing.home_score}–{existing.away_score}</span>
              </div>
            )}

            <button
              onClick={() => submit(fx)}
              disabled={submitting[fx.id]}
              data-testid="match-predict-featured-submit"
              className="relative w-full mt-5 bg-[#F4D03F] hover:bg-yellow-300 disabled:opacity-40 text-black text-xs font-mono uppercase tracking-[0.25em] font-bold py-3 flex items-center justify-center gap-2"
            >
              {isAuthed ? (
                <><Zap className="w-4 h-4" /> {existing ? "Update your call" : "Lock the call"}</>
              ) : (
                <><Lock className="w-4 h-4" /> Log in to lock this call</>
              )}
            </button>
          </div>
        );
      })()}

      {/* Personal KPIs — only when signed in */}
      {isAuthed && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="match-predict-kpis">
          {[
            { key: "points", label: "Total points", val: totals.points, color: "#F4D03F", icon: Trophy },
            { key: "exact", label: "Exact scores", val: totals.exact, color: "#00FFB2", icon: Target },
            { key: "correct", label: "Winners called", val: totals.correct, color: "#00F0FF", icon: CheckCircle },
            { key: "streak", label: "Active streak", val: totals.active_streak, color: "#FF007F", icon: Flame },
            { key: "rank", label: "League rank", val: myRank || "—", color: "#B084FF", icon: Crown },
          ].map(({ key, label, val, color, icon: Icon }) => (
            <div key={key} data-testid={`match-predict-kpi-${key}`}
                 className="border-l-2 bg-[#0A0A10] p-3" style={{ borderLeftColor: color }}>
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" style={{ color }} />
                <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500">{label}</div>
              </div>
              <div className="font-heading font-black text-3xl mt-1" style={{ color }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {!isAuthed && (
        <div
          data-testid="match-predict-guest-banner"
          className="border border-[#F4D03F]/40 bg-[#F4D03F]/5 p-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <Lock className="w-4 h-4 text-[#F4D03F] shrink-0" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">Guest mode</div>
              <div className="text-sm text-gray-300 mt-0.5">
                Browse fixtures and the leaderboard freely — sign in to lock in picks and start scoring points.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to="/login"
              data-testid="match-predict-guest-login"
              className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#F4D03F] hover:bg-yellow-300 px-3 py-1.5 font-bold"
            >
              Log in
            </Link>
            <Link
              to="/register"
              data-testid="match-predict-guest-register"
              className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F] hover:bg-[#F4D03F] hover:text-black px-3 py-1.5 font-bold"
            >
              Join
            </Link>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#232330]" data-testid="match-predict-tabs">
        {[
          ["upcoming", `Upcoming (${upcoming.length})`],
          ...(isAuthed ? [["history", `My history (${mine.predictions?.length || 0})`]] : []),
          ["board", `Leaderboard (${board.length})`],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`match-predict-tab-${k}`}
            className={`px-4 py-2 text-[10px] font-mono uppercase tracking-widest transition-colors border-b-2 ${
              tab === k ? "border-[#F4D03F] text-[#F4D03F]" : "border-transparent text-gray-500 hover:text-white"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* Upcoming picks */}
      {tab === "upcoming" && (
        <div className="grid gap-3 md:grid-cols-2" data-testid="match-predict-upcoming">
          {upcoming.length === 0 && (
            <div className="col-span-full text-center py-10 text-[10px] font-mono uppercase tracking-widest text-gray-600">
              No upcoming matches to predict.
            </div>
          )}
          {upcoming.map((f) => {
            const home = teamById(f.home_team_id);
            const away = teamById(f.away_team_id);
            const existing = predictionByFxId[f.id];
            const draft = drafts[f.id] || { home: existing?.home_score ?? "", away: existing?.away_score ?? "" };
            const kickoff = new Date(f.scheduled_at);
            return (
              <div key={f.id} data-testid={`match-predict-card-${f.id}`}
                   className="border border-[#232330] bg-[#0A0A10] p-4 space-y-3 hover:border-[#F4D03F] transition-colors">
                <div className="flex justify-between items-center text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  <span>{kickoff.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  {f.map && <span className="text-gray-400">{f.map}</span>}
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                  <div className="text-right">
                    <div className="font-heading text-lg font-black" style={{ color: home?.logo_color }}>{home?.tag || "TBD"}</div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500 truncate">{home?.name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" min="0" max="99"
                      value={draft.home}
                      onChange={(e) => setDraft(f.id, "home", e.target.value)}
                      data-testid={`match-predict-home-${f.id}`}
                      className="w-12 bg-[#05050A] border border-[#232330] focus:border-[#F4D03F] outline-none rounded-none py-1 text-center font-mono text-lg font-bold text-white"
                    />
                    <span className="text-gray-600 font-mono">:</span>
                    <input
                      type="number" min="0" max="99"
                      value={draft.away}
                      onChange={(e) => setDraft(f.id, "away", e.target.value)}
                      data-testid={`match-predict-away-${f.id}`}
                      className="w-12 bg-[#05050A] border border-[#232330] focus:border-[#F4D03F] outline-none rounded-none py-1 text-center font-mono text-lg font-bold text-white"
                    />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-black" style={{ color: away?.logo_color }}>{away?.tag || "TBD"}</div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500 truncate">{away?.name}</div>
                  </div>
                </div>
                {existing && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 text-center">
                    Currently locked: <span className="text-cyan-400">{existing.home_score}–{existing.away_score}</span>
                  </div>
                )}
                {(() => {
                  const poll = polls[f.id];
                  if (!poll || !poll.total) return null;
                  const top = poll.top || [];
                  const o = poll.outcome || {};
                  return (
                    <div
                      data-testid={`match-predict-poll-${f.id}`}
                      className="border-t border-[#232330] pt-3 space-y-2"
                    >
                      <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-gray-500">
                        <span>Community poll</span>
                        <span className="text-gray-400">{poll.total} pick{poll.total === 1 ? "" : "s"}</span>
                      </div>
                      <div className="flex gap-1 text-[9px] font-mono uppercase tracking-widest">
                        <div className="flex-1 bg-[#05050A] border border-[#232330] px-2 py-1">
                          <span className="text-gray-500">{home?.tag || "H"} </span>
                          <span className="text-cyan-400 font-bold">{o.home_win?.pct ?? 0}%</span>
                        </div>
                        <div className="flex-1 bg-[#05050A] border border-[#232330] px-2 py-1 text-center">
                          <span className="text-gray-500">Draw </span>
                          <span className="text-gray-300 font-bold">{o.draw?.pct ?? 0}%</span>
                        </div>
                        <div className="flex-1 bg-[#05050A] border border-[#232330] px-2 py-1 text-right">
                          <span className="text-[#FF007F] font-bold">{o.away_win?.pct ?? 0}%</span>
                          <span className="text-gray-500"> {away?.tag || "A"}</span>
                        </div>
                      </div>
                      {top.length > 0 && (
                        <div className="space-y-1">
                          {top.map((b, i) => {
                            const barColor = b.home > b.away ? "#00F0FF" : b.away > b.home ? "#FF007F" : "#9CA3AF";
                            return (
                              <div
                                key={`${b.home}-${b.away}`}
                                data-testid={`match-predict-poll-bucket-${f.id}-${i}`}
                                className="relative overflow-hidden bg-[#05050A] border border-[#232330] px-2 py-1"
                              >
                                <div
                                  className="absolute inset-y-0 left-0 opacity-20"
                                  style={{ width: `${b.pct}%`, background: barColor }}
                                />
                                <div className="relative flex items-center justify-between text-[10px] font-mono">
                                  <span className="font-bold" style={{ color: barColor }}>
                                    {b.home}&ndash;{b.away}
                                  </span>
                                  <span className="text-gray-400">{b.pct}% · {b.count}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <button
                  onClick={() => submit(f)}
                  disabled={submitting[f.id]}
                  data-testid={`match-predict-submit-${f.id}`}
                  className="w-full bg-[#F4D03F] hover:bg-yellow-300 disabled:opacity-40 text-black text-[11px] font-mono uppercase tracking-widest font-bold py-2 flex items-center justify-center gap-1"
                >
                  {isAuthed ? (
                    <><Zap className="w-3.5 h-3.5" /> {existing ? "Update pick" : "Lock in pick"}</>
                  ) : (
                    <><Lock className="w-3.5 h-3.5" /> Log in to lock pick</>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* My history */}
      {tab === "history" && isAuthed && (
        <div className="border border-[#232330]" data-testid="match-predict-history">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-2 border-b border-[#232330] bg-[#0a0a12] text-[10px] font-mono uppercase tracking-widest text-gray-500">
            <span>Match</span><span>Pick</span><span>Pts</span><span>Status</span>
          </div>
          {(mine.predictions || []).length === 0 && (
            <div className="p-6 text-center text-[10px] font-mono uppercase tracking-widest text-gray-600">No predictions yet.</div>
          )}
          {(mine.predictions || []).map((p) => {
            const fx = completed.find((f) => f.id === p.fixture_id) || fixtures.find((f) => f.id === p.fixture_id);
            const home = fx && teamById(fx.home_team_id);
            const away = fx && teamById(fx.away_team_id);
            const status = !p.scored ? "pending" : (p.points === 3 ? "exact" : p.points === 1 ? "outcome" : "miss");
            const statusColor = { pending: "text-gray-500", exact: "text-[#00FFB2]", outcome: "text-cyan-400", miss: "text-red-500" }[status];
            return (
              <div key={p.fixture_id} data-testid={`match-predict-history-${p.fixture_id}`}
                   className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-2 border-b border-[#1a1a22] text-xs font-mono">
                <span className="truncate">
                  <span style={{ color: home?.logo_color }}>{home?.tag || "?"}</span> vs <span style={{ color: away?.logo_color }}>{away?.tag || "?"}</span>
                </span>
                <span className="text-white">{p.home_score}–{p.away_score}</span>
                <span className="text-[#F4D03F] font-bold">{p.scored ? `+${(p.points || 0) + (p.bonus || 0)}` : "—"}</span>
                <span className={`uppercase tracking-widest text-[9px] ${statusColor}`}>{status}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Leaderboard */}
      {tab === "board" && (
        <div className="border border-[#232330]" data-testid="match-predict-leaderboard">
          <div className="grid grid-cols-[40px_1fr_auto_auto_auto_auto] gap-3 px-4 py-2 border-b border-[#232330] bg-[#0a0a12] text-[10px] font-mono uppercase tracking-widest text-gray-500">
            <span>#</span><span>Rep</span><span>Pts</span><span>Exact</span><span>Called</span><span>Streak</span>
          </div>
          {board.length === 0 && (
            <div className="p-6 text-center text-[10px] font-mono uppercase tracking-widest text-gray-600">No entries yet — get predicting.</div>
          )}
          {board.map((r) => {
            const me = isAuthed && r.user_id === user.id;
            return (
              <div key={r.user_id} data-testid={`match-predict-board-${r.rank}`}
                   className={`grid grid-cols-[40px_1fr_auto_auto_auto_auto] gap-3 px-4 py-2 border-b border-[#1a1a22] text-xs font-mono ${me ? "bg-[#F4D03F]/10 border-l-2 border-l-[#F4D03F]" : ""}`}>
                <span className={`font-heading font-black ${r.rank === 1 ? "text-[#F4D03F]" : r.rank === 2 ? "text-gray-300" : r.rank === 3 ? "text-orange-400" : "text-gray-500"}`}>#{r.rank}</span>
                <span className="truncate">{r.name}{me && <span className="text-[#F4D03F] ml-1">(you)</span>}</span>
                <span className="text-white font-bold">{r.points}</span>
                <span className="text-[#00FFB2]">{r.exact}</span>
                <span className="text-cyan-400">{r.correct}</span>
                <span className="text-[#FF007F] flex items-center gap-0.5">{r.active_streak > 0 && <Flame className="w-3 h-3" />}{r.active_streak}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
